/**
 * Track — the scrubber rail. Pointer-captured scrubbing, a
 * hover preview, and `role="slider"` so it is reachable and operable from the
 * keyboard without the native controls the theater drops.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';

import { cn } from '../../../../../lib/utils';
import { formatTimecode } from '../../../../../lib/media/timecode';
import type { NotesSession } from '../session/useNotesSession';
import { capturePointer } from './pointerCapture';
import type { PreviewSnapshot } from './FrameSnapshot';
import { clamp, clampToRail, isPortrait, previewEdgeInsetPx, type PreviewNote } from './previewFrame';
import { ScrubPreview, type PreviewPlacement } from './ScrubPreview';
import { useMediaTime, type MediaController } from './useMediaController';

/** A scrub must never land exactly on the duration — `[0, duration − ε]`. */
const END_EPSILON_MS = 1;
const FULL_PCT = 100;
const START_MS = 0;
const SLIDER_LABEL = 'Seek';
const STEP_BACK_KEY = 'ArrowLeft';
const STEP_FORWARD_KEY = 'ArrowRight';
const PLAY_PAUSE_KEY = ' ';
const START_KEY = 'Home';
const END_KEY = 'End';
const SEEK_PAUSED = { pause: true };

/** A point the preview floats above; `leftPx` is from the rail's left edge, already clamped. */
export interface PreviewTarget {
  tMs: number;
  leftPx: number;
}

/** A hovered note marker: the preview lifts above it, showing its captured frame when it has one. */
export interface MarkerPreview extends PreviewTarget {
  snapshot?: PreviewSnapshot;
  note?: PreviewNote;
}

export interface TrackProps {
  session: NotesSession;
  /** The player's media — the hover preview seeks a hidden copy of it. */
  src: string;
  crossOrigin?: 'anonymous';
  /** The marker row sits on the rail's own width, so its hover drives the same preview. */
  markerPreview?: MarkerPreview | null;
}

function hasDuration(durationMs: number): boolean {
  return Number.isFinite(durationMs) && durationMs > 0;
}

function placementOf(markerPreview: MarkerPreview | null): PreviewPlacement {
  return markerPreview ? 'marker' : 'rail';
}

function togglePlayback(controller: MediaController): void {
  if (controller.paused) controller.play();
  else controller.pause();
}

/**
 * Seeks paused, with at most one seek in flight. A drag produces a target on
 * every pointer move, and each new seek aborts the one still running, so the
 * picture would never catch up; instead a target that arrives mid-seek waits,
 * replacing any older waiting one, and goes out when the running seek lands.
 */
function useLatestSeek(seek: MediaController['seek']): (tMs: number) => void {
  const queuedRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const drain = useCallback(async () => {
    inFlightRef.current = true;
    try {
      let target = queuedRef.current;
      while (target !== null) {
        queuedRef.current = null;
        await seek(target, SEEK_PAUSED);
        target = queuedRef.current;
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [seek]);

  // A target still waiting when the rail goes away (a capture hides the
  // transport) must not move the playhead after it.
  useEffect(() => {
    return () => {
      queuedRef.current = null;
    };
  }, []);

  return useCallback(
    (tMs: number) => {
      queuedRef.current = tMs;
      if (inFlightRef.current) return;
      void drain();
    },
    [drain],
  );
}

export function Track({ session, src, crossOrigin, markerPreview = null }: TrackProps) {
  const { controller, frameStep } = session;
  const currentTimeMs = useMediaTime(controller);
  const railRef = useRef<HTMLDivElement | null>(null);
  const [scrubbing, setScrubbing] = useState(false);
  const [hover, setHover] = useState<PreviewTarget | null>(null);
  // Not `session.seekTo`: that flashes the stage, which strobes during a drag.
  const seekPaused = useLatestSeek(controller.seek);

  const railRect = useCallback((): DOMRect | null => {
    const rail = railRef.current;
    if (!rail) return null;
    const rect = rail.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return rect;
  }, []);

  /** Null until the rail has a width and the media has reported a duration. */
  const timeAtX = useCallback(
    (clientX: number): number | null => {
      const rect = railRect();
      if (!rect || !hasDuration(controller.durationMs)) return null;
      const ratio = (clientX - rect.left) / rect.width;
      return clamp(ratio * controller.durationMs, 0, controller.durationMs - END_EPSILON_MS);
    },
    [railRect, controller.durationMs],
  );

  const portrait = isPortrait(controller.intrinsic);

  const showHoverAt = useCallback(
    (clientX: number, tMs: number) => {
      const rect = railRect();
      if (!rect) return;
      setHover({ tMs, leftPx: clampToRail(clientX - rect.left, rect.width, previewEdgeInsetPx(portrait)) });
    },
    [railRect, portrait],
  );

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      // Before `loadedmetadata` there is no timeline to land on; seeking to 0
      // would silently rewind a video the reviewer has not even seen yet.
      const tMs = timeAtX(e.clientX);
      if (tMs === null) return;

      setScrubbing(true);
      // Pointer capture keeps the drag alive past the rail's own 18px height.
      capturePointer(e.currentTarget, e.pointerId);
      seekPaused(tMs);
    },
    [seekPaused, timeAtX],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      const tMs = timeAtX(e.clientX);
      if (tMs === null) return;
      if (scrubbing) {
        seekPaused(tMs);
        return;
      }
      showHoverAt(e.clientX, tMs);
    },
    [scrubbing, timeAtX, seekPaused, showHoverAt],
  );

  // The last target of the drag is already queued behind the running seek.
  const stopScrubbing = useCallback(() => setScrubbing(false), []);
  const hidePreview = useCallback(() => setHover(null), []);

  // Home / End need a timeline, exactly like a click on the rail.
  const seekToEdge = useCallback(
    (edge: typeof START_KEY | typeof END_KEY) => {
      if (!hasDuration(controller.durationMs)) return;
      seekPaused(edge === START_KEY ? START_MS : controller.durationMs - END_EPSILON_MS);
    },
    [controller.durationMs, seekPaused],
  );

  // The global map stands down for `role="slider"` — Space and the arrows
  // included — so while the rail has focus these are the only handlers for
  // its keys: no double step, and Space never falls through to scroll.
  const keyActions = useMemo(
    () =>
      new Map<string, () => void>([
        [STEP_BACK_KEY, () => frameStep(-1)],
        [STEP_FORWARD_KEY, () => frameStep(1)],
        [PLAY_PAUSE_KEY, () => togglePlayback(controller)],
        [START_KEY, () => seekToEdge(START_KEY)],
        [END_KEY, () => seekToEdge(END_KEY)],
      ]),
    [frameStep, controller, seekToEdge],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const action = keyActions.get(e.key);
      if (!action) return;
      e.preventDefault();
      action();
    },
    [keyActions],
  );

  const progressPct = hasDuration(controller.durationMs) ? clamp((currentTimeMs / controller.durationMs) * FULL_PCT, 0, FULL_PCT) : 0;
  const durationForAria = hasDuration(controller.durationMs) ? Math.round(controller.durationMs) : 0;
  // A drag shows the frame on the stage itself; a hovered marker wins over the rail.
  const railPreview = scrubbing ? null : hover;
  const previewTarget = markerPreview ?? railPreview;

  return (
    <div
      ref={railRef}
      role="slider"
      tabIndex={0}
      aria-label={SLIDER_LABEL}
      aria-valuemin={0}
      aria-valuemax={durationForAria}
      aria-valuenow={Math.round(currentTimeMs)}
      aria-valuetext={formatTimecode(currentTimeMs)}
      className="group relative flex h-[18px] cursor-pointer touch-none items-center"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stopScrubbing}
      onPointerCancel={stopScrubbing}
      onPointerLeave={hidePreview}
      onKeyDown={onKeyDown}
    >
      <div className="absolute inset-x-0 h-[3px] rounded-full bg-white/[.18] transition-[height] duration-150 group-hover:h-[5px] motion-reduce:transition-none" />
      <div
        className="absolute left-0 h-[3px] rounded-full bg-white transition-[height] duration-150 group-hover:h-[5px] motion-reduce:transition-none"
        style={{ width: `${progressPct}%` }}
      />
      <div
        className={cn(
          'absolute size-[11px] -translate-x-[5.5px] scale-[.8] rounded-full bg-white transition-transform duration-150',
          'group-hover:scale-100 motion-reduce:transition-none',
        )}
        style={{ left: `${progressPct}%` }}
      />
      <ScrubPreview
        key={src}
        visible={previewTarget !== null}
        timeMs={previewTarget?.tMs ?? 0}
        leftPx={previewTarget?.leftPx ?? 0}
        src={src}
        crossOrigin={crossOrigin}
        ready={controller.ready}
        portrait={portrait}
        placement={placementOf(markerPreview)}
        snapshot={markerPreview?.snapshot}
        note={markerPreview?.note}
      />
    </div>
  );
}
